import { Disc3, ExternalLink } from "lucide-react";
import { VerifiedBadge } from "@/components/ui/verified-badge";
import { Logo } from "@/components/Logo";
import type { TemplateProps, AlbumItem } from "../types";

/* ─────────────────────────────────────────────────────────────
   Vendedor — layout editorial estilo "studio".
   Cabeçalho: nome à esquerda, bio à direita.
   Grid uniforme com APENAS os discos à venda, exibindo o preço
   real cadastrado no Marketplace.
   ───────────────────────────────────────────────────────────── */

function priceLabel(value: number | null) {
  if (value == null) return null;
  try {
    return value.toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
      maximumFractionDigits: 0,
    });
  } catch {
    return `R$ ${value}`;
  }
}

function Cover({ a }: { a: AlbumItem }) {
  return a.cover_url ? (
    <img
      src={a.cover_url}
      alt={`${a.title} — ${a.artist}`}
      loading="lazy"
      className="h-full w-full object-cover"
    />
  ) : (
    <div className="grid h-full w-full place-items-center bg-neutral-200 text-neutral-400">
      <Disc3 className="h-1/3 w-1/3" />
    </div>
  );
}

export function PersonaTemplate({ bio, config }: TemplateProps) {
  const name = config.title || bio.display_name || bio.username;
  const tagline =
    config.subtitle ||
    bio.bio ||
    "Discos selecionados à venda — entre em contato para combinar.";

  // Apenas itens realmente listados à venda (com seus preços reais)
  const items: AlbumItem[] = bio.for_sale;

  const contactHref = config.social.whatsapp
    ? `https://wa.me/${config.social.whatsapp.replace(/\D/g, "")}`
    : config.social.instagram
      ? `https://instagram.com/${config.social.instagram.replace(/^@/, "")}`
      : config.links[0]?.url;

  return (
    <div className="min-h-screen bg-[#eceae5] text-neutral-900">
      {/* HEADER */}
      <header className="flex items-start justify-between gap-6 px-6 pt-8 sm:px-12 sm:pt-10">
        <div className="flex items-center gap-2">
          <h1 className="font-display text-2xl font-medium tracking-tight sm:text-3xl">
            {name}
          </h1>
          {bio.is_premium && (
            <VerifiedBadge variant="shimmer" size={18} aria-label="Verificado" />
          )}
        </div>
        <div className="flex max-w-[320px] flex-col items-end gap-3 sm:max-w-sm">
          <p className="text-right text-xs leading-relaxed text-neutral-600 sm:text-sm">
            {tagline}
          </p>
          {contactHref && (
            <a
              href={contactHref}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 rounded-full bg-neutral-900 px-3.5 py-1.5 text-[11px] font-medium text-white hover:bg-neutral-800"
            >
              Entrar em contato <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
      </header>

      {/* GRID */}
      <section className="px-6 py-10 sm:px-12 sm:py-14">
        {items.length === 0 ? (
          <div className="grid place-items-center rounded-xl border border-dashed border-neutral-300 py-32 text-center text-sm text-neutral-500">
            <div>
              <div className="font-medium text-neutral-700">Nenhum disco à venda ainda</div>
              <p className="mt-2 max-w-sm text-xs">
                Marque discos como "À venda" no Marketplace e eles aparecerão aqui automaticamente com o preço cadastrado.
              </p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-3 sm:gap-x-6 lg:grid-cols-4 xl:grid-cols-5">
            {items.map((a) => {
              const price = priceLabel(a.price_brl);
              return (
                <figure key={a.id} className="group">
                  <div className="aspect-square overflow-hidden rounded-sm bg-neutral-200 shadow-sm transition-transform duration-300 group-hover:-translate-y-0.5">
                    <Cover a={a} />
                  </div>
                  <figcaption className="mt-2.5 flex items-baseline justify-between gap-3">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium text-neutral-900">
                        {a.title}
                      </div>
                      <div className="truncate text-xs text-neutral-500">{a.artist}</div>
                    </div>
                    {price && (
                      <div className="shrink-0 text-sm font-semibold text-neutral-900">
                        {price}
                      </div>
                    )}
                  </figcaption>
                </figure>
              );
            })}
          </div>
        )}
      </section>

      {/* FOOTER */}
      <footer className="flex items-center justify-between border-t border-neutral-900/10 px-6 py-5 text-[10px] uppercase tracking-[0.22em] text-neutral-500 sm:px-12">
        <span>
          {items.length} {items.length === 1 ? "disco à venda" : "discos à venda"}
        </span>
        <a href="/" className="inline-flex items-center gap-2 hover:text-neutral-900">
          <span>feito por</span>
          <Logo variant="dark" size={14} withLink={false} />
        </a>
      </footer>
    </div>
  );
}
